import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { LiveKitProvider } from "@/contexts/LiveKitContext";
import LiveKitFloatingBar from "@/components/LiveKitFloatingBar";
import ProtectedRoute from "@/components/ProtectedRoute";

// Auth
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

// Public Pages
import PublicLayout from "@/components/PublicLayout";
import PublicHomePage from "@/pages/PublicHomePage";
import EventsMediaPage from "@/pages/EventsMediaPage";
import BadgePage from "@/pages/BadgePage";
import PlatformPage from "@/pages/PlatformPage";
import FeaturesPage from "@/pages/FeaturesPage";
import SectorPage from "@/pages/SectorPage";
import { BlogListPage, BlogArticlePage } from "@/pages/BlogPage";
import ContactPage from "@/pages/ContactPage";
import QuotePage from "@/pages/QuotePage";
import { PrivacyPage, CookiesPage, LegalPage } from "@/pages/LegalPages";
import SeoAuditPage from "@/pages/SeoAuditPage";
import PricingPage from "@/pages/public/PricingPage";
import QuestionnairePage from "@/pages/public/QuestionnairePage";
import PortfolioPage from "@/pages/public/PortfolioPage";
import SupportAccessPage from "@/pages/SupportAccessPage";

// Dashboard
import DashboardLayout from "@/layouts/DashboardLayout";
import KPIDashboardPage from "@/pages/app/KPIDashboardPage";
import { ProjectsListPage } from "@/pages/app/ProjectsPage";
import ProjectDetailPage from "@/pages/app/ProjectsPage";
import TicketsPage from "@/pages/app/TicketsPage";
import TicketDetailPage from "@/pages/app/TicketDetailPage";
import MessagingPage from "@/pages/app/MessagingPage";
import FilesPage from "@/pages/app/FilesPage";
import SEOPage from "@/pages/app/SEOPage";
import InvoicesPage from "@/pages/app/InvoicesPage";
import ClientsPage from "@/pages/app/ClientsPage";
import DocumentsPage from "@/pages/app/DocumentsPage";
import LeadsPage from "@/pages/app/LeadsPage";
import AIAssistantPage from "@/pages/app/AIAssistantPage";
import PricingPlansPage from "@/pages/app/PricingPlansPage";
import ClientDetailPage from "@/pages/app/ClientDetailPage";
import AffiliatesPage from "@/pages/app/AffiliatesPage";
import ProposalsPage from "@/pages/app/ProposalsPage";
import SocialMediaPage from "@/pages/app/SocialMediaPage";
import CompetitorsPage from "@/pages/app/CompetitorsPage";
import AgenciesPage from "@/pages/app/AgenciesPage";
import EmailDashboardPage from "@/pages/app/EmailDashboardPage";
import ProjectInboxPage from "@/pages/app/ProjectInboxPage";
import ClientSocialPage from "@/pages/app/ClientSocialPage";
import GuidePage from "@/pages/app/GuidePage";
import ProposalSettingsPage from "@/pages/app/ProposalSettingsPage";
import OnboardingPage from "@/pages/app/OnboardingPage";
import SubscriptionPage from "@/pages/app/SubscriptionPage";
import SuperAdminPage from "@/pages/app/SuperAdminPage";
import QuestionnairesAdminPage from "@/pages/app/QuestionnairesAdminPage";
import ServiceContractsPage from "@/pages/app/ServiceContractsPage";
import ProfilePage from "@/pages/app/ProfilePage";
import ProjectControlCenter from "@/pages/app/ProjectControlCenter";
import RegisterPage from "@/pages/RegisterPage";
import SignContractPage from "@/pages/public/SignContractPage";
import EventLandingPage from "@/pages/EventLandingPage";
import ActivateAccountPage from "@/pages/ActivateAccountPage";
import EventCenterPage from "@/pages/app/EventCenterPage";
import AdCampaignsPage from "@/pages/app/AdCampaignsPage";
import OrgDashboardPage from "@/pages/app/OrgDashboardPage";
import OrgEventsPage from "@/pages/app/OrgEventsPage";
import OrgEventDetailPage from "@/pages/app/OrgEventDetailPage";
import OrgScannerPage from "@/pages/app/OrgScannerPage";
import OrgMarketplacePage from "@/pages/app/OrgMarketplacePage";
import AdminMarketplacePage from "@/pages/app/AdminMarketplacePage";
import OrgTeamPage from "@/pages/app/OrgTeamPage";
import PaymentResultPage from "@/pages/PaymentResultPage";
import PublicEventPage from "@/pages/PublicEventPage";
import PublicEventsListPage from "@/pages/PublicEventsListPage";
import CancelRegistrationPage from "@/pages/CancelRegistrationPage";
import LiveScreenPage from "@/pages/app/LiveScreenPage";
import AdminOrgDetailPage from "@/pages/app/AdminOrgDetailPage";
import AdminOrgEventsPage from "@/pages/app/AdminOrgEventsPage";
import LiveControlPage from "@/pages/app/LiveControlPage";
import BroadcastDisplayPage from "@/pages/app/BroadcastDisplayPage";
import ContactsReferentielPage from "@/pages/app/ContactsReferentielPage";
import MediaShowcaseAdminPage from "@/pages/app/MediaShowcaseAdminPage";
import NoWorkspacePage from "@/pages/app/NoWorkspacePage";
import OrgProfilePage from "@/pages/app/OrgProfilePage";
import NewPricingPage from "@/pages/PricingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import EventJoinPage from "@/pages/EventJoinPage";
import LiveMeetingPage from "@/pages/app/LiveMeetingPage";
import ControlCenterPage from "@/pages/app/ControlCenterPage";
import ProgramOutputPage from "@/pages/app/ProgramOutputPage";
import DetachedPanelPage from "@/pages/app/DetachedPanelPage";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import GalleriesPage from "@/pages/app/GalleriesPage";
import GalleryDetailPage from "@/pages/app/GalleryDetailPage";
import PhotoStudioPage from "@/pages/app/PhotoStudioPage";
import PublicGalleryPage from "@/pages/public/PublicGalleryPage";

// Smart index redirect based on active workspace
function DashboardIndexRedirect() {
  const { user, activeWorkspace, workspaces } = useAuth();
  if (!user) return null;

  // No workspaces = no active module → onboarding/activation page
  if (!workspaces || workspaces.length === 0) {
    return <NoWorkspacePage />;
  }

  // Route based on active workspace
  if (activeWorkspace === "event_center") {
    // For admin users viewing event center, show the admin event center page
    if (user.role === "admin") return <EventCenterPage />;
    // For org roles, show their org dashboard
    return <OrgDashboardPage />;
  }

  // Default: web workspace
  if (user.role === "admin") return <KPIDashboardPage />;
  return <KPIDashboardPage />;
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
    <AuthProvider>
      <LiveKitProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicLayout><PublicHomePage /></PublicLayout>} />
            <Route path="/plateforme" element={<PublicLayout><PlatformPage /></PublicLayout>} />
            <Route path="/fonctionnalites" element={<PublicLayout><FeaturesPage /></PublicLayout>} />
            <Route path="/secteurs/:sectorSlug" element={<PublicLayout><SectorPage /></PublicLayout>} />
            <Route path="/blog" element={<PublicLayout><BlogListPage /></PublicLayout>} />
            <Route path="/blog/:slug" element={<PublicLayout><BlogArticlePage /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
            <Route path="/devis" element={<PublicLayout><QuotePage /></PublicLayout>} />
            <Route path="/politique-confidentialite" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
            <Route path="/politique-cookies" element={<PublicLayout><CookiesPage /></PublicLayout>} />
            <Route path="/mentions-legales" element={<PublicLayout><LegalPage /></PublicLayout>} />
            <Route path="/audit" element={<PublicLayout><SeoAuditPage /></PublicLayout>} />
            <Route path="/tarifs" element={<PublicLayout><PricingPage /></PublicLayout>} />
            <Route path="/realisations" element={<PublicLayout><PortfolioPage /></PublicLayout>} />
            <Route path="/questionnaire/:token" element={<QuestionnairePage />} />
            <Route path="/inscription" element={<RegisterPage />} />
            <Route path="/register" element={<Navigate to="/inscription" replace />} />
            <Route path="/signer/:token" element={<SignContractPage />} />
            <Route path="/event" element={<PublicLayout><EventLandingPage /></PublicLayout>} />
            <Route path="/evenements-medias" element={<PublicLayout><EventsMediaPage /></PublicLayout>} />
            <Route path="/evenements" element={<PublicEventsListPage />} />
            <Route path="/e/:orgSlug/:eventSlug" element={<PublicEventPage />} />
            <Route path="/cancel/:token" element={<CancelRegistrationPage />} />
            <Route path="/badge/:qrToken" element={<BadgePage />} />
            <Route path="/broadcast/:sessionId" element={<BroadcastDisplayPage />} />
            <Route path="/program-output/:sessionId" element={<ProgramOutputPage />} />
            <Route path="/detached-panel/:sessionId" element={<DetachedPanelPage />} />
            <Route path="/pricing" element={<NewPricingPage />} />
            <Route path="/pricing/:module" element={<NewPricingPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/event-join/:eventId/:joinToken" element={<EventJoinPage />} />
            <Route path="/payment/cancel" element={<PaymentResultPage />} />

            <Route path="/g/:shareToken" element={<PublicGalleryPage />} />
            <Route path="/studio/:galleryId" element={<ProtectedRoute><PhotoStudioPage /></ProtectedRoute>} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/activer-compte" element={<ActivateAccountPage />} />
            <Route path="/assistance" element={<SupportAccessPage />} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Protected Dashboard Routes */}
            <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<DashboardIndexRedirect />} />
              <Route path="dashboard" element={<KPIDashboardPage />} />
              <Route path="projets" element={<ProjectsListPage />} />
              <Route path="projets/:projectId" element={<ProjectDetailPage />} />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="seo" element={<SEOPage />} />
              <Route path="seo/:projectId" element={<SEOPage />} />
              <Route path="tickets" element={<TicketsPage />} />
              <Route path="tickets/:id" element={<TicketDetailPage />} />
              <Route path="messagerie" element={<MessagingPage />} />
              <Route path="fichiers" element={<FilesPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="contrats" element={<ServiceContractsPage />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="questionnaires" element={<QuestionnairesAdminPage />} />
              <Route path="assistant" element={<AIAssistantPage />} />
              <Route path="factures" element={<InvoicesPage />} />
              <Route path="plans" element={<PricingPlansPage />} />
              <Route path="clients/:clientId" element={<ClientDetailPage />} />
              <Route path="affilies" element={<AffiliatesPage />} />
              <Route path="propositions" element={<ProposalsPage />} />
              <Route path="reseaux-sociaux" element={<SocialMediaPage />} />
              <Route path="concurrents" element={<CompetitorsPage />} />
              <Route path="agences" element={<AgenciesPage />} />
              <Route path="emails" element={<EmailDashboardPage />} />
              <Route path="inbox-projets" element={<ProjectInboxPage />} />
              <Route path="mes-reseaux" element={<ClientSocialPage />} />
              <Route path="guide" element={<GuidePage />} />
              <Route path="parametrages-propositions" element={<ProposalSettingsPage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route path="abonnement" element={<SubscriptionPage />} />
              <Route path="super-admin" element={<SuperAdminPage />} />
              <Route path="control-center" element={<ProjectControlCenter />} />
              <Route path="event-center" element={<EventCenterPage />} />
              <Route path="event-center/org/:orgId" element={<AdminOrgDetailPage />} />
              <Route path="event-center/org/:orgId/events" element={<AdminOrgEventsPage />} />
              <Route path="event-center/org/:orgId/event/:eventId" element={<OrgEventDetailPage />} />
              <Route path="event-center/org/:orgId/event/:eventId/live" element={<LiveScreenPage />} />
              <Route path="event-center/org/:orgId/event/:eventId/live-control" element={<LiveControlPage />} />
              <Route path="event-center/org/:orgId/scanner" element={<OrgScannerPage />} />
              <Route path="regie-publicitaire" element={<AdCampaignsPage />} />
              {/* Org Admin: Event Management */}
              <Route path="evenements" element={<OrgEventsPage />} />
              <Route path="evenements/:eventId" element={<OrgEventDetailPage />} />
              <Route path="evenements/:eventId/live" element={<LiveScreenPage />} />
              <Route path="evenements/:eventId/live-control" element={<LiveControlPage />} />
              <Route path="scanner" element={<OrgScannerPage />} />
              <Route path="marketplace" element={<OrgMarketplacePage />} />
              <Route path="admin-prestataires" element={<AdminMarketplacePage />} />
              <Route path="equipe" element={<OrgTeamPage />} />
              <Route path="contacts-referentiel" element={<ContactsReferentielPage />} />
              <Route path="vitrine-medias" element={<MediaShowcaseAdminPage />} />
              <Route path="profil" element={<ProfilePage />} />
              <Route path="profil-organisation" element={<OrgProfilePage />} />
              <Route path="live-meeting/:roomId" element={<LiveMeetingPage />} />
              <Route path="control-center/:eventId" element={<ControlCenterPage />} />
              <Route path="galeries" element={<GalleriesPage />} />
              <Route path="galeries/:galleryId" element={<GalleryDetailPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" theme="dark" />
          <LiveKitFloatingBar />
          <PWAInstallPrompt />
        </BrowserRouter>
      </LiveKitProvider>
      </AuthProvider>
    </I18nProvider>
    </div>
  );
}

export default App;
